import { Check, ChevronDown } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Dual-mode dropdown pattern for the Library Filter-Bar (Batch 8 —
 * 3 consumers in Commit 2: child / year / month). Promote-from-day-1
 * per FB-5 ≥2-consumer rule; ships in `components/patterns/` from
 * the start.
 *
 * **Two usage modes** (both supported by the same component):
 *
 * **`mode='single'`** — single-select with optional "Alle" exclusive
 * default:
 *   - Picking an option clears any prior selection (single-set)
 *   - `allOption` (when provided) acts as the "no filter" sentinel
 *   - Selecting `allOption` clears all individual selections
 *   - Selecting an individual option clears `allOption`
 *   - Used by: Library Year-Dropdown + Month-Dropdown.
 *
 * **`mode='multi'`** — multi-select with optional "Alle" exclusive
 * default:
 *   - Options toggle independently (checkbox-style)
 *   - `allOption` is mutually exclusive with individual selections
 *     (same exclusion logic as single-mode)
 *   - Used by: Library Children-Dropdown.
 *
 * The naming "MultiSelectDropdown" arguably mismatches `mode='single'`
 * — but renaming to "FilterDropdown" or similar would create churn
 * for downstream consumers. Promote to a separate component if a 4th
 * pure-single-select consumer surfaces with no `allOption` semantics.
 *
 * **`testId`** is raw — caller composes the full selector
 * (`'library-child-filter'`, `'library-year-filter'`, …) so existing
 * test selectors stay stable across consumers.
 */
export interface MultiSelectDropdownOption<T> {
  value: T;
  label: string;
  /** Optional avatar/icon node rendered left of the label. */
  avatar?: ReactNode;
  /** Optional count rendered right of the label (e.g. "Lukas · 32 Geschichten"). */
  count?: number;
}

export interface MultiSelectDropdownProps<T> {
  options: Array<MultiSelectDropdownOption<T>>;
  selected: Set<T>;
  onChange: (selected: Set<T>) => void;
  mode: 'single' | 'multi';
  /**
   * Optional "Alle" exclusive default. When this option is selected,
   * all individual options are deselected (and vice versa). Use
   * `allOption.value` as a sentinel — it should NOT also appear in
   * the regular `options` list.
   */
  allOption?: { label: string; value: T };
  /** Trigger button label rendered when no selection is active. */
  triggerLabel: string;
  /**
   * Optional placeholder rendered alongside `triggerLabel` when no
   * selection is active. When omitted, only `triggerLabel` shows.
   */
  placeholder?: string;
  className?: string;
  /** Raw — caller composes the full selector. */
  testId?: string;
}

export function MultiSelectDropdown<T extends string>({
  options,
  selected,
  onChange,
  mode,
  allOption,
  triggerLabel,
  placeholder,
  className,
  testId,
}: MultiSelectDropdownProps<T>) {
  const [open, setOpen] = useState(false);

  const handleAllToggle = () => {
    if (!allOption) return;
    const isAll = selected.has(allOption.value);
    if (isAll) {
      // Clicking the active "Alle" leaves selection empty (caller
      // can interpret empty = no filter).
      onChange(new Set());
    } else {
      onChange(new Set([allOption.value]));
    }
    if (mode === 'single') setOpen(false);
  };

  const handleOptionToggle = (value: T) => {
    const next = new Set(selected);
    // Selecting an individual option always clears the "Alle" sentinel.
    if (allOption) next.delete(allOption.value);

    if (mode === 'single') {
      next.clear();
      next.add(value);
      onChange(next);
      setOpen(false);
    } else {
      // multi mode: toggle independently
      if (next.has(value)) next.delete(value);
      else next.add(value);
      onChange(next);
    }
  };

  // Trigger label resolution — show selected label(s) when present,
  // otherwise the placeholder + triggerLabel.
  const triggerText = (() => {
    const hasSelection =
      selected.size > 0 && (!allOption || !(selected.size === 1 && selected.has(allOption.value)));
    if (allOption && selected.has(allOption.value) && selected.size === 1) {
      return allOption.label;
    }
    if (!hasSelection) {
      return placeholder ? `${triggerLabel}: ${placeholder}` : triggerLabel;
    }
    if (mode === 'single') {
      const sole = options.find((o) => selected.has(o.value));
      return sole ? `${triggerLabel}: ${sole.label}` : triggerLabel;
    }
    // multi mode: show count of selected
    return `${triggerLabel}: ${selected.size}`;
  })();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-9 items-center gap-1.5 rounded-md border border-dream-navy/15 bg-dream-white px-3 text-sm text-dream-navy hover:bg-dream-navy/5 transition-colors focus-visible:outline-2 focus-visible:outline-dream-navy/50',
            className,
          )}
          data-testid={testId}
        >
          <span className="truncate">{triggerText}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" strokeWidth={2} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="p-1 w-56"
        data-testid={testId ? `${testId}-content` : undefined}
      >
        {allOption && (
          <button
            type="button"
            onClick={handleAllToggle}
            className={cn(
              'flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-dream-navy/5 transition-colors',
              selected.has(allOption.value) && 'bg-dream-navy/5',
            )}
            data-testid={testId ? `${testId}-option-all` : undefined}
          >
            <span className="flex h-4 w-4 shrink-0 items-center justify-center">
              {selected.has(allOption.value) && (
                <Check className="h-3.5 w-3.5 text-dream-navy" strokeWidth={2.5} />
              )}
            </span>
            <span className="flex-1 text-dream-navy">{allOption.label}</span>
          </button>
        )}
        {allOption && options.length > 0 && (
          <div className="my-1 h-px bg-dream-navy/10" aria-hidden="true" />
        )}
        {options.map((opt) => {
          const isChecked = selected.has(opt.value);
          return (
            <button
              key={String(opt.value)}
              type="button"
              onClick={() => handleOptionToggle(opt.value)}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2 py-2 text-left text-sm hover:bg-dream-navy/5 transition-colors',
                isChecked && 'bg-dream-navy/5',
              )}
              data-testid={testId ? `${testId}-option-${opt.value}` : undefined}
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                {isChecked && <Check className="h-3.5 w-3.5 text-dream-navy" strokeWidth={2.5} />}
              </span>
              {opt.avatar && <span className="shrink-0">{opt.avatar}</span>}
              <span className="flex-1 truncate text-dream-navy">{opt.label}</span>
              {opt.count !== undefined && (
                <span className="shrink-0 text-xs text-dream-navy/60">{opt.count}</span>
              )}
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
